export type ReactNode = VNode | string | number | boolean | null | undefined | ReactNode[];
export type CSSProperties = Record<string, string | number | undefined>;
export type ButtonHTMLAttributes<T = HTMLButtonElement> = { className?: string; children?: ReactNode; disabled?: boolean; type?: string; [key: string]: any };

export type VNode = {
  type: string | symbol | Component;
  props: Record<string, unknown> & { children?: ReactNode[] };
};

type Component = (props: Record<string, unknown>) => ReactNode;
type EffectRecord = { deps?: unknown[]; cleanup?: () => void };
type ComponentState = { hooks: unknown[]; effects: EffectRecord[] };

export const Fragment = Symbol('Fragment');
export const StrictMode = Fragment;

const componentState = new Map<string, ComponentState>();
let currentInstance = '';
let currentHook = 0;
let rootContainer: HTMLElement | null = null;
let rootNode: ReactNode = null;
let renderQueued = false;
let effectQueue: Array<() => void> = [];

function flatten(input: ReactNode[]): ReactNode[] {
  const output: ReactNode[] = [];
  input.forEach((item) => {
    if (Array.isArray(item)) output.push(...flatten(item));
    else if (item !== false && item !== true && item !== null && item !== undefined) output.push(item);
  });
  return output;
}

export function createElement(
  type: VNode['type'],
  props: Record<string, unknown> | null,
  ...children: ReactNode[]
): VNode {
  const childList = flatten(children);
  return { type, props: { ...(props ?? {}), children: childList } };
}

function depsChanged(previous: unknown[] | undefined, next: unknown[] | undefined): boolean {
  if (!previous || !next || previous.length !== next.length) return true;
  return previous.some((value, index) => !Object.is(value, next[index]));
}

function getState(): ComponentState {
  const existing = componentState.get(currentInstance);
  if (existing) return existing;
  const created = { hooks: [], effects: [] };
  componentState.set(currentInstance, created);
  return created;
}

export function useState<T>(initial: T | (() => T)): [T, (next: T | ((current: T) => T)) => void] {
  const instance = currentInstance;
  const index = currentHook++;
  const state = getState();
  if (!(index in state.hooks)) state.hooks[index] = typeof initial === 'function' ? (initial as () => T)() : initial;
  const setState = (next: T | ((current: T) => T)) => {
    const target = componentState.get(instance);
    if (!target) return;
    const current = target.hooks[index] as T;
    target.hooks[index] = typeof next === 'function' ? (next as (current: T) => T)(current) : next;
    requestRender();
  };
  return [state.hooks[index] as T, setState];
}

export function useMemo<T>(factory: () => T, deps: unknown[]): T {
  const index = currentHook++;
  const state = getState();
  const record = state.hooks[index] as { value: T; deps: unknown[] } | undefined;
  if (!record || depsChanged(record.deps, deps)) {
    const next = { value: factory(), deps };
    state.hooks[index] = next;
    return next.value;
  }
  return record.value;
}

export function useEffect(effect: () => void | (() => void), deps?: unknown[]): void {
  const index = currentHook++;
  const state = getState();
  const previous = state.effects[index];
  if (!previous || depsChanged(previous.deps, deps)) {
    effectQueue.push(() => {
      previous?.cleanup?.();
      const cleanup = effect();
      state.effects[index] = { deps, cleanup: typeof cleanup === 'function' ? cleanup : undefined };
    });
  }
}

function setStyle(element: HTMLElement | SVGElement, styles: CSSProperties): void {
  Object.entries(styles).forEach(([key, value]) => {
    if (value === undefined) return;
    if (key.startsWith('--')) element.style.setProperty(key, String(value));
    else (element.style as unknown as Record<string, string>)[key] = typeof value === 'number' && !['opacity', 'zIndex', 'fontWeight'].includes(key) ? `${value}px` : String(value);
  });
}

function stableFieldKey(props: Record<string, unknown>): string | undefined {
  if (props.id) return `id:${String(props.id)}`;
  if (props.name) return `name:${String(props.name)}`;
  if (props['aria-label']) return `label:${String(props['aria-label'])}`;
  return undefined;
}

function preservedFieldKey(field: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement): string | undefined {
  if (field.dataset.nexoFieldKey) return `stable:${field.dataset.nexoFieldKey}`;
  if (field.dataset.nexoFieldPath) return `path:${field.dataset.nexoFieldPath}`;
  return undefined;
}

function appendNode(parent: Node, node: ReactNode, path: string): void {
  if (node === null || node === undefined || node === false || node === true) return;
  if (typeof node === 'string' || typeof node === 'number') {
    parent.appendChild(document.createTextNode(String(node)));
    return;
  }
  if (Array.isArray(node)) {
    node.forEach((child, index) => appendNode(parent, child, `${path}.${index}`));
    return;
  }
  // Treat every symbol-based JSX wrapper as a fragment. This keeps the
  // renderer compatible if a bundler emits Symbol(react.fragment) instead
  // of this module's local Fragment symbol.
  if (node.type === Fragment || typeof node.type === 'symbol') {
    (node.props.children ?? []).forEach((child, index) => appendNode(parent, child, `${path}.f${index}`));
    return;
  }
  if (typeof node.type === 'function') {
    const previousInstance = currentInstance;
    const previousHook = currentHook;
    currentInstance = `${path}:${node.type.name || 'component'}`;
    currentHook = 0;
    const output = node.type(node.props);
    currentInstance = previousInstance;
    currentHook = previousHook;
    appendNode(parent, output, `${path}.c`);
    return;
  }

  const isSvgNode = node.type === 'svg' || parent instanceof SVGElement;
  const element = isSvgNode
    ? document.createElementNS('http://www.w3.org/2000/svg', node.type as string)
    : document.createElement(node.type as string);
  if (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement || element instanceof HTMLSelectElement) {
    element.dataset.nexoFieldPath = path;
    const fieldKey = stableFieldKey(node.props);
    if (fieldKey) element.dataset.nexoFieldKey = fieldKey;
  }
  let deferredSelectValue: string | undefined;
  Object.entries(node.props).forEach(([key, value]) => {
    if (key === 'children' || key === 'key' || value === undefined || value === null || value === false) return;
    if (key === 'className') element.setAttribute('class', String(value));
    else if (key === 'strokeWidth') element.setAttribute('stroke-width', String(value));
    else if (key === 'strokeLinecap') element.setAttribute('stroke-linecap', String(value));
    else if (key === 'strokeLinejoin') element.setAttribute('stroke-linejoin', String(value));
    else if (key === 'htmlFor') element.setAttribute('for', String(value));
    else if (key === 'style' && typeof value === 'object') setStyle(element, value as CSSProperties);
    else if (key.startsWith('on') && typeof value === 'function') {
      let eventName = key.slice(2).toLowerCase();
      if (eventName === 'change' && ['INPUT', 'TEXTAREA'].includes(element.tagName.toUpperCase())) eventName = 'input';
      element.addEventListener(eventName, value as EventListener);
    } else if (key === 'disabled' && value) (element as HTMLButtonElement).disabled = true;
    else if (key === 'value' && element.tagName.toUpperCase() === 'SELECT') { deferredSelectValue = String(value); (element as HTMLElement).dataset.nexoControlledValue = 'true'; }
    else if (key === 'value') { (element as HTMLInputElement).value = String(value); (element as HTMLElement).dataset.nexoControlledValue = 'true'; }
    else if (key === 'checked') { (element as HTMLInputElement).checked = Boolean(value); (element as HTMLElement).dataset.nexoControlledChecked = 'true'; }
    else if (key === 'autoFocus' && value) queueMicrotask(() => (element as HTMLElement).focus());
    else if (key === 'target' || key === 'rel' || key === 'href' || key === 'id' || key === 'role' || key.startsWith('aria-') || key === 'placeholder' || key === 'inputMode' || key === 'title' || key === 'type') element.setAttribute(key, String(value));
    else if (value === true) element.setAttribute(key, '');
    else element.setAttribute(key, String(value));
  });
  (node.props.children ?? []).forEach((child, index) => appendNode(element, child, `${path}.${index}`));
  if (deferredSelectValue !== undefined) (element as HTMLSelectElement).value = deferredSelectValue;
  parent.appendChild(element);
}

function performRender(): void {
  if (!rootContainer) return;
  const activeElement = document.activeElement;
  const activeField = activeElement instanceof HTMLInputElement || activeElement instanceof HTMLTextAreaElement || activeElement instanceof HTMLSelectElement ? activeElement : null;
  const activeFieldPath = activeField?.dataset.nexoFieldPath;
  const activeFieldKey = activeField?.dataset.nexoFieldKey;
  const selection = activeField instanceof HTMLInputElement || activeField instanceof HTMLTextAreaElement
    ? { start: activeField.selectionStart, end: activeField.selectionEnd, direction: activeField.selectionDirection }
    : null;
  const preservedFields = new Map<string, { value: string; checked?: boolean }>();
  rootContainer.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>('[data-nexo-field-path]').forEach((field) => {
    if (field instanceof HTMLInputElement && field.type === 'file') return;
    const key = preservedFieldKey(field);
    if (key) preservedFields.set(key, { value: field.value, checked: field instanceof HTMLInputElement ? field.checked : undefined });
  });
  renderQueued = false;
  effectQueue = [];
  rootContainer.replaceChildren();
  appendNode(rootContainer, rootNode, '0');
  rootContainer.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>('[data-nexo-field-path]').forEach((field) => {
    const key = preservedFieldKey(field);
    const previous = key ? preservedFields.get(key) : undefined;
    if (!previous) return;
    if (!field.dataset.nexoControlledValue && !(field instanceof HTMLInputElement && field.type === 'file')) field.value = previous.value;
    if (field instanceof HTMLInputElement && previous.checked !== undefined && !field.dataset.nexoControlledChecked) field.checked = previous.checked;
  });
  if (activeFieldKey || activeFieldPath) {
    const fields = Array.from(rootContainer.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>('[data-nexo-field-path]'));
    const restored = (activeFieldKey ? fields.find((field) => field.dataset.nexoFieldKey === activeFieldKey) : undefined)
      ?? fields.find((field) => field.dataset.nexoFieldPath === activeFieldPath);
    if (restored && !restored.disabled) {
      restored.focus({ preventScroll: true });
      if (selection && (restored instanceof HTMLInputElement || restored instanceof HTMLTextAreaElement)) {
        try { restored.setSelectionRange(selection.start, selection.end, selection.direction ?? undefined); } catch { /* Algunos tipos de campo, como number, no exponen selecciÃ³n. */ }
      }
    }
  }
  const effects = [...effectQueue];
  effectQueue = [];
  effects.forEach((effect) => effect());
}

export function requestRender(): void {
  if (renderQueued) return;
  renderQueued = true;
  queueMicrotask(performRender);
}

export function mount(container: HTMLElement, node: ReactNode): void {
  rootContainer = container;
  rootNode = node;
  performRender();
}

const React = { createElement, Fragment, StrictMode, useState, useMemo, useEffect };
export default React;

declare global {
  namespace JSX {
    interface IntrinsicElements { [elementName: string]: Record<string, unknown>; }
    interface IntrinsicAttributes { key?: string | number; }
    interface ElementChildrenAttribute { children: {}; }
    type Element = VNode;
  }
}
